import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import dayjs from 'dayjs'
import { useBreakpoint } from '~/hooks'
import { getLonCirculation } from '~/apis'
import { formatNumber, formatPercentage } from '~/utils/numbers'
import { StyledChartsContainer } from './styled'
import styled from 'styled-components'
import IconUni from '~/assets/images/token-logo-uni.svg'
import IconSushi from '~/assets/images/token-logo-sushi.svg'
import * as echarts from 'echarts'

type EChartsOption = echarts.EChartsOption

const LonCirculationChart: React.FC = () => {
  const { isMobile } = useBreakpoint()
  const { t } = useTranslation()
  const chartRef = useRef(null)

  const { data: lonCirculation } = useSWR('getLonCirculation', () =>
    getLonCirculation(),
  )
  const convertedLonCirculation = useMemo(() => {
    if (!lonCirculation) return null

    const liquidityAmount =
      Number(lonCirculation.sushiPool) + Number(lonCirculation.uniPool)
    const othersAmount =
      Number(lonCirculation.circulation) -
      Number(lonCirculation.staked) -
      liquidityAmount

    return {
      total: Number(lonCirculation.circulation),
      staking: Number(lonCirculation.staked),
      others: othersAmount,
      liquidity: liquidityAmount,
    }
  }, [lonCirculation])

  const canvasSize = isMobile
    ? { width: 320, height: 210 }
    : { width: 266, height: 301.5 }

  const currentDate = dayjs()
  const formattedDate = currentDate.format('MMM D.YYYY')

  useEffect(() => {
    if (!convertedLonCirculation) return
    const data = Object.entries(convertedLonCirculation)
      .map(([name, amount]) => ({
        name,
        value: amount,
      }))
      .filter(({ name }) => {
        return name !== 'total'
      })

    const chart = echarts.init(chartRef.current)
    const options: EChartsOption = {
      tooltip: {
        trigger: 'item',
      },
      legend: {
        show: false,
      },
      series: [
        {
          name: 'LON Circulation',
          type: 'pie',
          radius: ['50%', '80%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 5,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: false,
          },
          tooltip: {
            textStyle: {
              color: 'rgba(95, 85, 169, 0.85)',
            },
            formatter: (params) => {
              const { name, value } = params
              const formattedName =
                name === 'staking'
                  ? 'economic_staking'
                  : name === 'liquidity'
                  ? 'liquidity_pools'
                  : name
              let percent
              // If only two decimal places are retained for display, the sum will be less than 100, so the others item is calculated using 100 minus the other items.
              if (name === 'others') {
                const { total, staking, liquidity } = convertedLonCirculation
                const stakingPercent = formatNumber(staking / total, 4)
                const liquidityPercent = formatNumber(liquidity / total, 4)
                const othersPercent = formatPercentage(
                  1 - Number(stakingPercent) - Number(liquidityPercent),
                  2,
                )
                percent = othersPercent
              } else
                percent = formatPercentage(
                  value / convertedLonCirculation.total,
                  2,
                )
              return `${t(formattedName)}: ${percent}`
            },
          },
          data,
        },
      ],
      color: ['#BD89F0', '#9AE6F2', '#6DAFFC'],
    }

    chart.setOption(options)

    // 在组件销毁时销毁图表实例
    return () => {
      chart.dispose()
    }
  }, [convertedLonCirculation, isMobile])

  return (
    <StyledChartsContainer>
      <div className="header">
        <p className="title">{t('staking_chart_lon_circulation_title')}</p>
      </div>
      <StyledContentContainer>
        <div ref={chartRef} style={canvasSize} />
        <div>
          <StyledDataHeader>
            <div className="total">
              {`${t('total')}: ${formatNumber(
                convertedLonCirculation?.total,
                2,
              )}`}
            </div>
            <div className="date">{formattedDate}</div>
          </StyledDataHeader>
          <StyledDataList>
            <StyledDataItem>
              <StyledDash color="#BD89F0" />
              <span className="label">{t('economic_staking')}</span>
              <StyledDivider />
              <span className="amount">
                {formatNumber(convertedLonCirculation?.staking, 2)}
              </span>
            </StyledDataItem>
            <StyledDataItem>
              <StyledDash color="#9AE6F2" />
              <span className="label">{t('others')}</span>
              <StyledDivider />
              <span className="amount">
                {formatNumber(convertedLonCirculation?.others, 2)}
              </span>
            </StyledDataItem>
            <StyledDataItem>
              <StyledDash color="#6DAFFC" />
              <span className="label">{t('liquidity_pools')}</span>
              <StyledDivider />
              <span className="amount">
                {formatNumber(lonCirculation?.uniPool, 2)}
              </span>
              <StyledIcon src={IconUni} />
            </StyledDataItem>
            <StyledDataItem>
              <StyledDash color="transparent" />
              <span className="label"></span>
              <StyledDivider />
              <span className="amount">
                {formatNumber(lonCirculation?.sushiPool, 2)}
              </span>
              <StyledIcon src={IconSushi} />
            </StyledDataItem>
          </StyledDataList>
        </div>
      </StyledContentContainer>
    </StyledChartsContainer>
  )
}

export default LonCirculationChart

const StyledContentContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;

  ${({ theme }) => theme.media.sm`
    flex-flow: column nowrap;
  `}
`

const StyledDataHeader = styled.div`
  font-size: 13px;

  .total {
    font-weight: 700;
    line-height: 21px;
    color: #bd89f0;
  }

  .date {
    font-weight: 400;
    line-height: 27px;
    color: #9598ab;
  }
`

const StyledDataList = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
  margin-top: 32px;

  ${({ theme }) => theme.media.sm`
    margin-top: 24px;
  `}
`

const StyledDataItem = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  color: rgba(95, 85, 169, 0.85);
  font-size: 13px;
  font-weight: 400;
  line-height: 21px;

  .label {
    width: 65px;
  }

  .amount {
    font-weight: 500;
    line-height: 27px;
  }
`

const StyledDash = styled.span<{ color: string }>`
  display: inline-block;
  margin-right: 11px;
  border-radius: 50%;
  width: 6px;
  height: 6px;
  background-color: ${(props) => props.color};
`

const StyledDivider = styled.span`
  display: inline-block;
  margin-right: 11px;
  height: 16px;
  width: 1px;
  background-color: rgba(0, 0, 0, 0.1);
`

const StyledIcon = styled.img`
  margin-left: 11px;
  width: 24px;
  height: 24px;
`
